/* Progressive enhancement: native blog/tag pagination works without JavaScript. */
if (!customElements.get('water-blog-catalog')) {
  customElements.define('water-blog-catalog', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.active) this.init(); });
    }

    init() {
      this.grid = this.querySelector('[data-blog-grid]');
      if (!this.grid) return;
      this.active = true;
      this.lifecycle = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.query = '';
      this.pageSize = Number(this.dataset.pageSize) || 12;
      this.originalCards = [...this.grid.children].map(node => node.cloneNode(true));
      this.heading = this.querySelector('[data-blog-heading]');
      this.originalHeading = this.heading.textContent;
      this.status = this.querySelector('[data-search-status]');
      this.empty = this.querySelector('[data-blog-empty]');
      this.nativePagination = this.querySelector('[data-native-pagination]');
      this.originalPaginationHidden = this.nativePagination.hidden;
      this.searchPagination = this.querySelector('[data-search-pagination]');
      this.clearButton = this.querySelector('[data-clear-search]');
      this.clearButton.hidden = true;
      this.editorialState = new Map();
      const options = { signal: this.lifecycle.signal };
      const isSearch = target => target?.dataset?.blogSearch === this.dataset.blogId;

      document.addEventListener('input', event => {
        if (!isSearch(event.target)) return;
        clearTimeout(this.timer);
        ++this.generation;
        const value = event.target.value;
        this.timer = setTimeout(() => this.search(value), 200);
      }, options);
      document.addEventListener('submit', event => {
        if (event.target.dataset.blogSearchForm !== this.dataset.blogId) return;
        event.preventDefault();
        clearTimeout(this.timer);
        this.search(event.target.querySelector('[data-blog-search]').value, true);
      }, options);
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-blog-topic]');
        if (!link || link.dataset.blogTopic !== this.dataset.blogId) return;
        const url = new URL(link.href);
        const query = this.inputs()[0]?.value.trim() || '';
        if (query) url.searchParams.set('query', query);
        else url.searchParams.delete('query');
        link.href = url.href;
      }, options);
      this.clearButton.addEventListener('click', () => {
        clearTimeout(this.timer);
        this.search('');
        this.inputs()[0]?.focus();
      }, options);
      this.querySelector('[data-search-previous]').addEventListener('click', () => this.renderResults(this.resultPage - 1, true), options);
      this.querySelector('[data-search-next]').addEventListener('click', () => this.renderResults(this.resultPage + 1, true), options);
      window.addEventListener('popstate', () => {
        clearTimeout(this.timer);
        this.search(new URL(location.href).searchParams.get('query') || '', false, false);
      }, options);
      // A hero can be reloaded independently in the theme editor.
      document.addEventListener('shopify:section:load', () => this.syncInputs(), options);
      const initialQuery = new URL(location.href).searchParams.get('query') || '';
      if (initialQuery) this.search(initialQuery, false, false);
    }

    inputs() {
      return [...document.querySelectorAll('[data-blog-search]')].filter(input => input.dataset.blogSearch === this.dataset.blogId);
    }

    syncInputs() {
      this.inputs().forEach(input => { if (input.value !== this.query) input.value = this.query; });
    }

    setEditorialHidden(hidden) {
      if (hidden) {
        document.querySelectorAll('[data-blog-editorial]').forEach(section => {
          if (section.dataset.blogEditorial !== this.dataset.blogId) return;
          if (!this.editorialState.has(section)) this.editorialState.set(section, section.hidden);
          section.hidden = true;
        });
      } else {
        this.editorialState.forEach((wasHidden, section) => { section.hidden = wasHidden; });
        this.editorialState.clear();
      }
    }

    restore() {
      this.grid.replaceChildren(...this.originalCards.map(node => node.cloneNode(true)));
      this.heading.textContent = this.originalHeading;
      this.nativePagination.hidden = this.originalPaginationHidden;
      this.searchPagination.hidden = true;
      this.empty.hidden = this.originalCards.length > 0;
      this.status.textContent = '';
      this.removeAttribute('aria-busy');
      this.setEditorialHidden(false);
    }

    async search(value, focus = false, updateUrl = true) {
      const generation = ++this.generation;
      this.query = value.trim();
      this.syncInputs();
      if (updateUrl) {
        const url = new URL(location.href);
        if (this.query) url.searchParams.set('query', this.query);
        else url.searchParams.delete('query');
        history.replaceState(history.state, '', url);
      }
      this.clearButton.hidden = !this.query;
      if (!this.query) { this.restore(); return; }
      this.setEditorialHidden(true);
      this.status.textContent = this.dataset.loading;
      this.setAttribute('aria-busy', 'true');
      try {
        const index = await this.loadIndex();
        if (!this.active || generation !== this.generation) return;
        const terms = this.normalize(this.query).split(/\s+/).filter(Boolean);
        this.results = index.filter(item => terms.every(term => item.text.includes(term)));
        this.renderResults(1, focus);
      } catch (error) {
        if (!this.active || generation !== this.generation) return;
        this.restore();
        this.status.textContent = this.dataset.error;
      } finally {
        if (generation === this.generation) this.removeAttribute('aria-busy');
      }
    }

    normalize(text) {
      return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    }

    loadIndex() {
      if (this.indexPromise) return this.indexPromise;
      const pending = this.buildIndex().catch(error => {
        if (this.indexPromise === pending) this.indexPromise = null; // A failed request must be retryable.
        throw error;
      });
      this.indexPromise = pending;
      return this.indexPromise;
    }

    async buildIndex() {
      const signal = this.lifecycle.signal;
      const cards = new Map();
      const add = nodes => nodes.forEach(node => {
        if (node.dataset.articleId) cards.set(node.dataset.articleId, node.cloneNode(true));
      });
      add(this.originalCards);
      const pages = Number(this.dataset.pages) || 1;
      const currentPage = Number(this.dataset.page) || 1;
      // Request this section only, preserving the current locale and native tag route.
      for (let page = 1; page <= pages; page++) {
        if (signal.aborted) throw new Error('Blog section disconnected');
        if (page === currentPage) continue;
        const url = new URL(location.href);
        url.searchParams.delete('query');
        url.searchParams.delete('q');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(this.dataset.pageParam || 'page', page);
        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, { once: true });
        if (signal.aborted) controller.abort();
        const timeout = setTimeout(abort, 15000);
        try {
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Blog section request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const catalog = [...doc.querySelectorAll('water-blog-catalog')].find(node => node.dataset.blogId === this.dataset.blogId);
          if (!catalog || Number(catalog.dataset.page) !== page) throw new Error('Invalid blog section response');
          add([...catalog.querySelectorAll('[data-blog-grid] [data-blog-card]')]);
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', abort);
        }
      }
      if (cards.size < Number(this.dataset.total)) throw new Error('Incomplete blog index');
      // Keep native newest-first order even when the visitor starts on page two.
      return [...cards.values()].sort((a, b) => Number(b.dataset.published) - Number(a.dataset.published)).map(node => ({ node, text: this.normalize(node.dataset.searchText || node.textContent) }));
    }

    renderResults(page, focus = false) {
      if (!this.results) return;
      const pages = Math.max(1, Math.ceil(this.results.length / this.pageSize));
      this.resultPage = Math.max(1, Math.min(page, pages));
      const offset = (this.resultPage - 1) * this.pageSize;
      this.grid.replaceChildren(...this.results.slice(offset, offset + this.pageSize).map(item => item.node.cloneNode(true)));
      this.heading.textContent = (this.dataset.resultsLabel || '').replace('{query}', this.query);
      this.status.textContent = `${this.results.length} ${this.results.length === 1 ? this.dataset.singular : this.dataset.plural}`;
      this.empty.hidden = this.results.length > 0;
      this.nativePagination.hidden = true;
      this.searchPagination.hidden = pages <= 1;
      this.querySelector('[data-search-page]').textContent = `${this.resultPage} / ${pages}`;
      this.querySelector('[data-search-previous]').disabled = this.resultPage <= 1;
      this.querySelector('[data-search-next]').disabled = this.resultPage >= pages;
      if (focus) this.heading.focus();
    }

    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      ++this.generation;
      clearTimeout(this.timer);
      this.lifecycle.abort();
      this.restore();
      this.indexPromise = null;
    }
  });
}
